/**
 * @typedef {import('./MortgageInformation').default} MortgageInformation
 * @typedef {import('./MortgageInformationCardProps').default} Props
 * @typedef {import('./MortgageInformationCardState').default} State
 */

import { translate } from 'App/Internationalization';
import { withProperty } from '../PropertyProvider';
import { withPropertyService } from '../../../service-container';
import captureError from '../../../utils/captureError';
import {
	calculateEstimatedCurrentBalance,
	calculateEstimatedCurrentEquity,
	getLatestQuantariumValueInDollars,
} from '../../shared/FinanceWidgets/financeWidgetAttributes';
import images from 'config/local/images';
import LinkButton from 'shared/LinkButton';
import Loading from '../../../shared/Loading';
import mapMortgageInformation from './mapMortgageInformation';
import paths from 'config/local/paths';
import React from 'react';

/** @extends {React.Component<Props, State>} */
export class MortgageInformationCard extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		this.state = {
			error: false,
			isGettingData: true,
			mortgageInfo: null,
		};

		this.getMortgageInfo = this.getMortgageInfo.bind(this);
	}

	componentDidMount() {
		this.getMortgageInfo();
	}

	/**
	 * @param {Props} prevProps
	 * @returns {void}
	 */
	componentDidUpdate(prevProps) {
		const prevPropertyId =
			prevProps.match?.params?.propertyId ??
			prevProps.property?.selectedProperty?.id;

		if (this.#getPropertyId() === prevPropertyId) {
			return;
		}

		this.setState({
			error: false,
			isGettingData: true,
			mortgageInfo: [],
		});

		this.getMortgageInfo();
	}

	/** @returns {string | undefined} */
	#getPropertyId() {
		return (
			this.props.match?.params?.propertyId ??
			this.props.property?.selectedProperty?.id
		);
	}

	async getMortgageInfo() {
		try {
			const propertyId = this.#getPropertyId();

			if (!propertyId) {
				throw new Error(
					translate(
						'homeowner.pages.dashboard.properties.property_info_error'
					)
				);
			}

			const mortgageInfo =
				await this.props.propertyService.getEditablePropertyById(
					propertyId
				);
			const valuations = await this.props.propertyService
				.getValuations(propertyId)
				.catch(() => null);
			const estimatedCurrentBalance = calculateEstimatedCurrentBalance(
				mortgageInfo.details ?? {}
			);
			const estimatedCurrentEquity = calculateEstimatedCurrentEquity(
				getLatestQuantariumValueInDollars(valuations),
				estimatedCurrentBalance
			);

			this.setState({
				isGettingData: false,
				mortgageInfo: mapMortgageInformation(mortgageInfo, {
					estimatedCurrentBalance,
					estimatedCurrentEquity,
				}),
			});
		} catch (error) {
			captureError(error);

			this.setState({
				error: true,
				isGettingData: false,
			});
		}
	}

	renderMortgageInfo() {
		if (!this.state.mortgageInfo?.length) {
			return (
				<h1>
					{translate('homeowner.pages.finances.view.mortgage.error')}
				</h1>
			);
		}

		return this.state.mortgageInfo.map(
			/**
			 *  @param {{footer: string | number, full?: boolean, header: string, id: number}} info
			 *  @returns {JSX.Element}
			 */ (info) => (
				<div
					className={`property-detail-item ${
						info.full ? 'width-full' : 'width-half'
					}`}
					key={info.id}
				>
					<dt className="property-detail-item-header">
						{info.header}
					</dt>
					<dd className="property-detail-item-footer">
						{info.footer}
					</dd>
				</div>
			)
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	render() {
		const propertyId = this.#getPropertyId();

		if (this.state.error) {
			return null;
		}

		return (
			<section className="mt-10">
				<header className="h-ms-4">
					{translate('homeowner.pages.finances.view.mortgage.title')}
				</header>
				<section className="card card-property-detail card-border mt-3">
					{this.state.isGettingData ? (
						<div className="card-body">
							<Loading />
						</div>
					) : (
						<React.Fragment>
							<dl className="card-body property-details text-start">
								{this.renderMortgageInfo()}
							</dl>
							<footer className="card-footer">
								<LinkButton
									className="button button-icon mx-auto"
									icon={images.icons.edit}
									iconClass="icon icon-sm icon-fill-fg"
									to={`${paths.app.homeowner.properties.edit}/${propertyId}`}
								>
									{translate(
										'homeowner.pages.finances.view.mortgage.edit_details'
									)}
								</LinkButton>
							</footer>
						</React.Fragment>
					)}
				</section>
			</section>
		);
	}
}

const MortgageInformationCardWithProperty = withProperty(
	MortgageInformationCard
);

export default withPropertyService(
	/** @type {React.ComponentType<any>} */ (
		MortgageInformationCardWithProperty
	)
);
